// src/components/ResultModal.js
import React from 'react';
import { Trophy, XCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function ResultModal({ isOpen, onClose, resultsList = [] }) {
  const { t } = useTranslation();

  if (!isOpen || !resultsList || resultsList.length === 0) return null;

  // محاسبه مجموع سود/زیان کل شرط‌های این راند
  const totalNetProfit = resultsList.reduce((acc, curr) => acc + (curr.netProfit || 0), 0);
  const totalWins = resultsList.filter((item) => item.isWin).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="bg-[#1B152B] border border-white/10 w-full max-w-sm rounded-3xl p-5 shadow-2xl text-center relative overflow-hidden max-h-[85vh] flex flex-col">
        {/* Glow Background Effect */}
        <div
          className={`absolute -top-12 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full blur-3xl opacity-30 ${
            totalNetProfit >= 0 ? 'bg-emerald-500' : 'bg-rose-500'
          }`}
        />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white bg-white/5 hover:bg-white/10 p-2 rounded-full transition-all z-10"
        >
          <X size={18} />
        </button>

        {/* Top Header / Summary */}
        <div className="mb-4 relative pt-2">
          <div className="flex justify-center mb-2">
            {totalNetProfit >= 0 ? (
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border-2 border-emerald-500/40 flex items-center justify-center text-emerald-400">
                <Trophy size={30} />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-full bg-rose-500/10 border-2 border-rose-500/40 flex items-center justify-center text-rose-400">
                <XCircle size={30} />
              </div>
            )}
          </div>

          <h2 className={`text-xl font-black ${totalNetProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {totalNetProfit >= 0 ? t('youWon') : t('roundsResult')}
          </h2>
          <p className="text-xs text-white/60 mt-1">
            {resultsList.length > 1
              ? t('multiDuelsSummary', { count: resultsList.length, wins: totalWins })
              : t('singleDuelSummary', {
                  symbol: resultsList[0]?.symbol?.replace('USDT', ''),
                  position: resultsList[0]?.position === 'UP' ? t('up') : t('down'),
                })}
          </p>
        </div>

        {/* خلاصه کل سود / زیان (اگر بیشتر از ۱ شرط باشد) */}
        {resultsList.length > 1 && (
          <div
            className={`py-2.5 px-4 rounded-xl mb-3 border flex items-center justify-between text-xs font-bold ${
              totalNetProfit >= 0
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
            }`}
          >
            <span>{t('netProfitLoss')}:</span>
            <span className="text-sm font-black font-mono">
              {totalNetProfit >= 0 ? `+$${totalNetProfit.toFixed(2)}` : `-$${Math.abs(totalNetProfit).toFixed(2)}`}
            </span>
          </div>
        )}

        {/* لیست تمام شرط‌های تسویه شده */}
        <div className="overflow-y-auto space-y-3 my-2 pr-1 custom-scrollbar text-left">
          {resultsList.map((res, index) => {
            const cryptoName = res.symbol ? res.symbol.replace('USDT', '') : '';
            return (
              <div
                key={res.roundId || index}
                className={`p-3.5 rounded-2xl border bg-[#120F1D]/80 backdrop-blur-sm transition-all ${
                  res.isWin ? 'border-emerald-500/30' : 'border-rose-500/30'
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-sm text-white">{cryptoName}</span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                        res.position === 'UP' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
                      }`}
                    >
                      {res.position === 'UP' ? t('up') : t('down')}
                    </span>
                  </div>

                  <div className={`text-xs font-black font-mono ${res.isWin ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {res.isWin ? `+$${parseFloat(res.reward).toFixed(2)}` : `-$${parseFloat(res.betAmount).toFixed(2)}`}
                  </div>
                </div>

                {/* Target vs Close Price */}
                <div className="grid grid-cols-2 gap-2 bg-black/30 p-2 rounded-xl text-[11px] border border-white/5">
                  <div>
                    <span className="text-white/50 block text-[9px]">{t('entryPrice')}:</span>
                    <span className="font-mono font-bold text-white/90">${res.targetPrice}</span>
                  </div>
                  <div>
                    <span className="text-white/50 block text-[9px]">{t('closePrice')}:</span>
                    <span className="font-mono font-bold text-white/90">${res.closePrice}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Confirm Button */}
        <button
          onClick={onClose}
          className={`w-full mt-3 py-3 rounded-2xl font-black text-sm transition-all shadow-lg shrink-0 ${
            totalNetProfit >= 0
              ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-emerald-500/30'
              : 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-600/30'
          }`}
        >
          {t('continue')}
        </button>
      </div>
    </div>
  );
}

export default ResultModal;